import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react'
import { Popover as PopoverPrimitive } from 'radix-ui'
import { type ComponentProps, useState } from 'react'
import { DayPicker } from 'react-day-picker'
import { arSA, enUS } from 'react-day-picker/locale'
import { useDocumentDirection } from '../../hooks/use-document-direction'
import { cn } from '../../lib/utils'

/** A real calendar date from its parts, or null (31 February is null). */
function dateOf(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : null
}

/** `YYYY-MM-DD` to a local calendar date, or null for an empty or malformed value. */
function parseIsoDate(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '')
  return match ? dateOf(Number(match[1]), Number(match[2]), Number(match[3])) : null
}

/**
 * What a person types: `YYYY-MM-DD`, or day first (`15/7/2026`, `15-07-2026`,
 * `15.07.2026`), the order Egypt and the Gulf write. Arabic-Indic digits count.
 */
function parseTypedDate(text: string): Date | null {
  const latin = text.trim().replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
  const iso = parseIsoDate(latin)
  if (iso) return iso
  const match = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(latin)
  return match ? dateOf(Number(match[3]), Number(match[2]), Number(match[1])) : null
}

/** A local calendar date to `YYYY-MM-DD`, the API's date shape. */
function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** The text a person edits: day first, `DD/MM/YYYY`. */
function editableDate(date: Date): string {
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
}

/**
 * The field's text at rest: day, short month and year in the screen's
 * language, with Latin digits in both languages (the app's numeral rule), as
 * `formatDate` prints dates elsewhere.
 */
function displayDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-EG' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    numberingSystem: 'latn',
  }).format(date)
}

type DateFieldProps = Omit<
  ComponentProps<'input'>,
  'value' | 'onChange' | 'type' | 'min' | 'max'
> & {
  /** `YYYY-MM-DD`, or null when empty. */
  value: string | null
  onChange: (value: string | null) => void
  /** The screen's language: month names and the field text follow it. */
  locale: 'en' | 'ar' | string
  /** Names the calendar button (an icon button needs a name). */
  calendarLabel?: string
  /** Offer a clear button when a date is set; `clearLabel` names it. */
  clearable?: boolean
  clearLabel?: string
  /** The earliest and latest dates allowed, `YYYY-MM-DD`. */
  min?: string
  max?: string
  invalid?: boolean
}

/**
 * A date field that reads the same in every browser (decision D8,
 * react-day-picker). A person can type the date (`15/07/2026` or ISO) or pick
 * it from a calendar in the screen's language and direction; at rest the field
 * shows the app's own date text. A native `<input type="date">` showed the
 * browser's format (`10/29/2026`) and digits instead. The form gets `YYYY-MM-DD`
 * as soon as the text is a whole valid date inside `min`/`max`, and null when
 * the text is cleared.
 */
function DateField({
  value,
  onChange,
  locale,
  calendarLabel,
  clearable = false,
  clearLabel,
  min,
  max,
  invalid = false,
  disabled,
  className,
  onFocus,
  onBlur,
  ...props
}: DateFieldProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string | null>(null)
  const direction = useDocumentDirection()
  const selected = parseIsoDate(value)
  const minDate = parseIsoDate(min)
  const maxDate = parseIsoDate(max)
  const inRange = (date: Date) => (!minDate || date >= minDate) && (!maxDate || date <= maxDate)
  const disabledDays = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ]
  const PreviousIcon = direction === 'rtl' ? ChevronRightIcon : ChevronLeftIcon
  const NextIcon = direction === 'rtl' ? ChevronLeftIcon : ChevronRightIcon
  const text = draft ?? (selected ? displayDate(selected, locale) : '')

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <div className={cn('relative w-full', className)}>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            disabled={disabled}
            aria-invalid={invalid || undefined}
            value={text}
            onFocus={(event) => {
              setDraft(selected ? editableDate(selected) : '')
              onFocus?.(event)
            }}
            onBlur={(event) => {
              setDraft(null)
              onBlur?.(event)
            }}
            onChange={(event) => {
              const next = event.target.value
              setDraft(next)
              if (next.trim() === '') {
                onChange(null)
                return
              }
              const parsed = parseTypedDate(next)
              if (parsed && inRange(parsed)) onChange(toIsoDate(parsed))
            }}
            className={cn(
              'h-control w-full min-w-0 rounded-sm border border-input bg-input-background ps-2.5 text-body text-foreground tabular-nums',
              'transition-ui placeholder:text-muted-foreground enabled:hover:border-foreground',
              'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
              'aria-invalid:border-danger aria-invalid:enabled:hover:border-danger',
              'disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground',
              clearable && selected ? 'pe-16' : 'pe-9',
            )}
            {...props}
          />
          {clearable && selected && !disabled ? (
            <button
              type="button"
              aria-label={clearLabel ?? 'Clear date'}
              onClick={() => onChange(null)}
              className="absolute inset-y-0 end-8 my-auto flex size-6 items-center justify-center rounded-sm text-muted-foreground transition-ui hover:text-foreground focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <XIcon aria-hidden="true" className="size-3.5" />
            </button>
          ) : null}
          <PopoverPrimitive.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={calendarLabel ?? 'Open calendar'}
              className="absolute inset-y-0 end-1.5 my-auto flex size-7 items-center justify-center rounded-sm text-muted-foreground transition-ui enabled:hover:text-foreground focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed"
            >
              <CalendarIcon aria-hidden="true" className="size-4" />
            </button>
          </PopoverPrimitive.Trigger>
        </div>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          dir={direction}
          className="z-50 rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-popover"
        >
          <DayPicker
            mode="single"
            selected={selected ?? undefined}
            defaultMonth={selected ?? minDate ?? undefined}
            onSelect={(date) => {
              onChange(date ? toIsoDate(date) : null)
              setOpen(false)
            }}
            disabled={disabledDays}
            startMonth={minDate ?? undefined}
            endMonth={maxDate ?? undefined}
            locale={locale === 'ar' ? arSA : enUS}
            numerals="latn"
            dir={direction}
            showOutsideDays
            components={{
              Chevron: ({ orientation }) =>
                orientation === 'left' ? (
                  <PreviousIcon aria-hidden="true" className="size-4" />
                ) : (
                  <NextIcon aria-hidden="true" className="size-4" />
                ),
            }}
            classNames={{
              months: 'relative',
              month_caption: 'flex h-8 items-center justify-center text-label font-medium',
              nav: 'absolute inset-x-0 top-0 flex h-8 items-center justify-between',
              button_previous:
                'flex size-8 items-center justify-center rounded-sm text-muted-foreground transition-ui hover:bg-accent hover:text-foreground disabled:opacity-40',
              button_next:
                'flex size-8 items-center justify-center rounded-sm text-muted-foreground transition-ui hover:bg-accent hover:text-foreground disabled:opacity-40',
              month_grid: 'mt-2 border-collapse',
              weekdays: 'text-small text-muted-foreground',
              weekday: 'size-9 font-normal',
              day: 'p-0 text-center',
              day_button:
                'size-9 rounded-sm text-body tabular-nums transition-ui hover:bg-accent focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
              // Selection is `--brand` (packages/ui AGENTS.md).
              selected:
                '[&>button]:bg-brand [&>button]:text-brand-foreground [&>button]:hover:bg-brand',
              today: '[&>button]:font-semibold [&>button]:underline [&>button]:underline-offset-4',
              outside: 'text-muted-foreground opacity-60',
              disabled: 'pointer-events-none text-muted-foreground opacity-40',
            }}
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

export { DateField, type DateFieldProps }
